import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "api/axios";
import { Role } from "types";
import Loader from "components/Loader";
import { handleAxiosAndCaughtError } from "utils";
import { useTranslation } from "react-i18next";

interface RoleDetailsContextType {
  roleInfo: Role | null;
  isLoading: boolean;
  error: string | null;
  editRole: (data: Partial<Role>) => Promise<void>;
  deactivateRole: () => Promise<void>;
  fetchRoleDetails: () => Promise<void>;
  activateRole: () => Promise<void>;
}

const RoleDetailsContext = createContext<RoleDetailsContextType | undefined>(
  undefined
);

export const useRoleInfo = () => {
  const context = useContext(RoleDetailsContext);
  if (!context) {
    throw new Error("useRoleInfo must be used within a RoleDetailsProvider");
  }
  return context;
};

interface RoleDetailsProviderProps {
  children: ReactNode;
}

export const RoleInfoProvider = ({ children }: RoleDetailsProviderProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [roleInfo, setRoleInfo] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchRoleDetails = useCallback(async () => {
    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      setIsLoading(true);
      setError(null);
      // TODO: temporary for backward compatibility
      const resp = await api.get(`/groups/${id}`, {
        signal: controller.signal,
      });
      setRoleInfo(resp?.data);
    } catch (err: any) {
      if (err.name === "CanceledError" || err.name === "CanceledError") {
        // Request was aborted, do not set error or redirect
        return;
      }
      setError(t("roles.roleInfo.errors.failedToFetchRoleDetails"));
      handleAxiosAndCaughtError(err);
      navigate("/dashboard/roles", { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, id, t]);

  useEffect(() => {
    if (id && !roleInfo) {
      fetchRoleDetails();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id, fetchRoleDetails, roleInfo]);

  const editRole = async (data: Partial<Role>) => {
    try {
      setIsLoading(true);
      setError(null);
      // TODO: temporary for backward compatibility
      const response = await api.put<Role>(`/groups/${id}`, data);
      setRoleInfo(response.data);
    } catch (err) {
      setError(t("roles.roleInfo.errors.failedToUpdateRoleDetails"));
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const activateRole = async () => {
    try {
      setError(null);
      // TODO: temporary for backward compatibility
      await api.put(`/groups/${id}/activate`);

      setRoleInfo((prev) => {
        return {
          ...prev,
          is_active: true,
        };
      });
    } catch (err) {
      throw err;
    }
  };
  const deactivateRole = async () => {
    try {
      setError(null);
      // TODO: temporary for backward compatibility
      await api.put(`/groups/${id}/deactivate`);

      setRoleInfo((prev) => {
        return {
          ...prev,
          is_active: false,
        };
      });
    } catch (err) {
      throw err;
    }
  };

  const value = {
    roleInfo,
    isLoading,
    error,
    editRole,
    deactivateRole,
    activateRole,
    fetchRoleDetails,
  };

  return (
    <RoleDetailsContext.Provider value={value}>
      {isLoading ? (
        <Loader
          className="absolute left-1/2 top-1/2 -translate-y-1/2 translate-x-1/2"
          txt={t("roles.roleInfo.loadingRoleInformation")}
        />
      ) : (
        children
      )}
    </RoleDetailsContext.Provider>
  );
};
